// src/utils/sopralluoghi.js
// ═══════════════════════════════════════════════════════════════════════════
// SOPRALLUOGHI SENZA COMMESSA (ottobre 2026, W416)
// ═══════════════════════════════════════════════════════════════════════════
// Un sopralluogo può nascere prima della commessa (per un cliente o un potenziale cliente),
// come già le Richieste Commerciali e le Elaborazioni Offerta: projectId vuoto. Quando la
// commessa nasce lo si collega e da quel momento è come se fosse nato lì (scheda Sopralluogo,
// Cabina di Regia, Report). Facoltativamente è legato alla Richiesta Commerciale da cui nasce
// (rcId, rcNumero). Finché è senza commessa non entra in Cabina, costi o Insight: lì si legge
// per commessa.
// Chi lo crea: permesso «sopralluoghi-senza-commessa» (di default PM; titolare e
// amministratori sempre; gli operativi che li fanno si abilitano uno per uno).
// ═══════════════════════════════════════════════════════════════════════════
import { doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { nomeNorm } from "./ditteEsterne";
import { clienteDellaCommessa } from "./segnalazioniCliente";

export const PERMESSO_SENZA_COMMESSA = "sopralluoghi-senza-commessa";

/** Il sopralluogo non è (ancora) legato a una commessa. */
export const senzaCommessa = (s) => !!s && !String(s.projectId || "").trim();

/** Il nome del cliente o potenziale cliente scritto sul sopralluogo. */
export const clienteDelSopralluogo = (s) => String(s?.clienteNome || s?.nomeCliente || "").trim();

/** Stesso cliente? Per id se ci sono entrambi, altrimenti per nome (forme societarie comprese). */
export function stessoCliente(s, { clienteId = "", clienteNome = "" } = {}) {
  if (s?.clienteId && clienteId) return s.clienteId === clienteId;
  const a = nomeNorm(clienteDelSopralluogo(s)), b = nomeNorm(clienteNome);
  return !!a && a === b;
}

/**
 * Sopralluoghi senza commessa da proporre a una commessa: dello stesso cliente, oppure nati da
 * una Richiesta Commerciale che ora è di questa commessa. Più recenti prima.
 */
export function daCollegareAllaCommessa(sopralluoghi = [], project = {}, rcDellaCommessa = [], progettiPerId = {}) {
  const rc = new Set((rcDellaCommessa || []).map(r => r.id || r).filter(Boolean));
  const cliente = clienteDellaCommessa(project || {}, progettiPerId);
  return (sopralluoghi || []).filter(s => senzaCommessa(s) && ((s.rcId && rc.has(s.rcId)) || stessoCliente(s, cliente)))
    .sort((a, b) => String(b.data || "").localeCompare(String(a.data || "")));
}

/** Commesse proposte per il collegamento: aperte, prima quelle dello stesso cliente o della stessa RC. */
export function commesseProposte(progetti = [], s, rcElenco = []) {
  const rc = s?.rcId ? (rcElenco || []).find(r => r.id === s.rcId) : null;
  const perId = Object.fromEntries((progetti || []).filter(Boolean).map(p => [p.id, p]));
  const punteggio = (p) => (rc?.projectId && rc.projectId === p.id ? 2 : 0) + (stessoCliente(s, clienteDellaCommessa(p, perId)) ? 1 : 0);
  return (progetti || []).filter(p => p && !p.isParent && !p.archived && p.statoCommessa !== "chiusa")
    .map(p => ({ p, punti: punteggio(p) }))
    .sort((a, b) => b.punti - a.punti || String(a.p.codiceCommessa || a.p.name || "").localeCompare(String(b.p.codiceCommessa || b.p.name || "")))
    .map(({ p, punti }) => ({ ...p, consigliata: punti > 0 }));
}

/** Collega il sopralluogo alla commessa: da qui in poi vive nella commessa. */
export async function collegaSopralluogo(s, project, utente = {}, progettiPerId = {}) {
  if (!s?.id || !project?.id) throw new Error("Sopralluogo o commessa mancante");
  const cl = clienteDellaCommessa(project, progettiPerId);
  await updateDoc(doc(db, "sopralluoghi", s.id), {
    projectId: project.id,
    projectName: project.name || "",
    codiceCommessa: project.codiceCommessa || "",
    clienteNome: cl.clienteNome || clienteDelSopralluogo(s),
    clienteId: cl.clienteId || s.clienteId || "",
    collegatoACommessa: { il: serverTimestamp(), uid: utente.uid || "", nome: utente.displayName || utente.nome || "" },
    updatedAt: serverTimestamp(),
  });
}

/** «RC-2026-0012 · Hotel Riviera» per mostrare una Richiesta Commerciale in un elenco. */
export const etichettaRC = (r) => [r?.numero, r?.clienteNome, r?.oggetto || r?.descrizione].filter(Boolean).join(" · ").slice(0, 120);
